// ============================================================================
// Shared two-phase document upload (request upload-url -> S3 PUT -> finalize).
// ============================================================================
// Extracted from AdminDocumentsPanel so any panel (S6b record-external) reuses
// the exact signed-checksum flow. The sha256 + derived MIME must flow to BOTH
// the upload-url payload AND the PUT header — the checksum and ContentType are
// bound into the presigned URL server-side, so a mismatch fails the PUT.
// ============================================================================

import { api, type DocumentUploadUrlInput } from '../api';

export async function sha256Base64(file: File): Promise<string> {
  const buf = await file.arrayBuffer();
  const digest = await crypto.subtle.digest('SHA-256', buf);
  let binary = '';
  for (const b of new Uint8Array(digest)) binary += String.fromCharCode(b);
  return btoa(binary);
}

// Browsers derive File.type from the OS registry, not the file: Office types
// (and .md) can come back empty. The API allowlist is strict and the presigned
// PUT binds ContentType, so ONE derived value flows to both the upload-url
// payload and the PUT header. PDFs/images are universally registered — trust
// the browser there.
const EXTENSION_MIME: Record<string, string> = {
  md: 'text/markdown',
  pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  // Fallbacks for the "universally registered" types (Copilot #17 r6): they are
  // normally supplied by the browser, but a stripped OS registry returns '' for
  // these too, and '' then fails the API's mime_type enum with an opaque 400
  // AFTER an upload URL has already been requested. Every entry here is in
  // DOCUMENT_MIME_TYPES (validate.ts:630) -- do not add a type the API rejects.
  pdf: 'application/pdf',
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  webp: 'image/webp',
  heic: 'image/heic',
  // csv is REQUIRED here, not optional (Copilot #17 r7). The UI advertises it
  // (AdminDocumentsPanel.tsx:645 accept list) and the API allows it
  // (DOCUMENT_MIME_TYPES, validate.ts:643) — but Windows commonly reports
  // File.type as '' or 'application/vnd.ms-excel' for .csv, neither of which
  // the server accepts. The map takes precedence over file.type, so this
  // resolves BOTH cases. Without it the guard below would throw on a file type
  // the platform genuinely supports.
  csv: 'text/csv',
};

export function documentMime(file: File): string {
  const ext = file.name.slice(file.name.lastIndexOf('.') + 1).toLowerCase();
  const mime = EXTENSION_MIME[ext] ?? file.type;
  // Fail loudly and EARLY rather than shipping '' into the upload-url request.
  // An empty mime fails the server's mime_type enum, which rejects it BEFORE
  // any URL is issued — so it cannot desync the presigned PUT's bound
  // ContentType (an earlier comment here claimed it could; that was wrong).
  // The value of throwing is a legible client-side message instead of an opaque
  // server 400, not the prevention of a signature mismatch.
  if (!mime) {
    throw new Error(
      `Could not determine the file type of "${file.name}". Rename it with a standard extension (e.g. .pdf) and try again.`,
    );
  }
  return mime;
}

export async function putToS3(uploadUrl: string, file: File, mimeType: string, sha256b64: string): Promise<void> {
  const res = await fetch(uploadUrl, {
    method: 'PUT',
    headers: {
      'Content-Type': mimeType,
      'x-amz-checksum-sha256': sha256b64,
    },
    body: file,
  });
  if (!res.ok) {
    // S3 errors carry an XML body (AccessDenied, BadDigest, …) — surface a
    // short slice of it; statusText is often empty over HTTP/2.
    const detail = (await res.text().catch(() => '')).slice(0, 300);
    const status = res.statusText ? `${res.status} ${res.statusText}` : `${res.status}`;
    throw new Error(`S3 upload failed (${status})${detail ? `: ${detail}` : ''}`);
  }
}

export type UploadMeta = Omit<DocumentUploadUrlInput, 'file_name' | 'mime_type' | 'byte_size' | 'sha256_base64'>;

// High-level: run the full cycle for one file, returning the finalized
// document_id. onPhase reports progress for a spinner ('' clears it).
export async function uploadDocument(
  token: string, file: File, meta: UploadMeta, onPhase?: (p: string) => void,
): Promise<string> {
  // finally, not a trailing call (Copilot #17 r6): every caller drives a
  // spinner off onPhase, so a throw anywhere below used to leave the phase
  // string set forever -- the upload appears to hang rather than to have
  // failed, which is the worst of both readings.
  try {
    onPhase?.('Computing checksum…');
    const hash = await sha256Base64(file);
    const mime = documentMime(file);
    onPhase?.('Requesting upload URL…');
    const t = await api.documentUploadUrl(token, {
      ...meta,
      file_name: file.name,
      mime_type: mime,
      byte_size: file.size,
      sha256_base64: hash,
    });
    onPhase?.('Uploading…');
    await putToS3(t.upload_url, file, mime, hash);
    onPhase?.('Verifying…');
    await api.documentFinalize(token, t.document_id, t.version_id);
    return t.document_id;
  } finally {
    onPhase?.('');
  }
}
